import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import AuthShell from "@/components/suffa/AuthShell";
import { useLanguage } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSeoTitle } from "@/lib/seo";
import "@/pages/Auth.css";

const PROFILE_FOR_OPTIONS = [
  "Myself",
  "My Son",
  "My Daughter",
  "My Brother",
  "My Sister",
  "Family Member",
];

const Register = () => {
  useSeoTitle("Register — Create Your Nikah Profile | Suffa Matrimony");
  const { t } = useLanguage();
  const { register } = useAuth();
  const [form, setForm] = useState({
    profileFor: "",
    fullName: "",
    email: "",
    mobile: "",
    password: "",
    confirmPassword: "",
    agree: false,
  });
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const validate = () => {
    const e = {};
    if (!form.profileFor) e.profileFor = "Please select who this profile is for.";
    if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
    else if (form.fullName.trim().length < 3)
      e.fullName = "Please enter your full name (at least 3 characters).";
    if (!form.email.trim()) e.email = "Please enter your email address.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim()))
      e.email = "Please enter a valid email address.";
    const digits = form.mobile.replace(/\D/g, "");
    if (!form.mobile.trim()) e.mobile = "Please enter your mobile number.";
    else if (digits.length < 8 || digits.length > 15)
      e.mobile = "Please enter a valid mobile number (8–15 digits).";
    if (!form.password) e.password = "Please create a password.";
    else if (form.password.length < 8)
      e.password = "Password must be at least 8 characters.";
    if (!form.confirmPassword) e.confirmPassword = "Please confirm your password.";
    else if (form.confirmPassword !== form.password)
      e.confirmPassword = "Passwords do not match.";
    if (!form.agree) e.agree = "Please agree to the Terms & Conditions and Privacy Policy.";
    return e;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setLoading(true);
    try {
      await register({
        profileFor: form.profileFor,
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        mobile: form.mobile,
        password: form.password,
      });
      navigate("/create-profile");
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const field = (key) => `auth-input${errors[key] ? " has-error" : ""}`;

  return (
    <AuthShell
      footer={
        <div className="auth-footer-nav">
          <p className="auth-footer">
            {t("Already have an account?")}{" "}
            <a href="/login" data-testid="register-to-login" className="auth-footer-link">
              Login
            </a>
          </p>
          <a href="/" className="auth-back" data-testid="register-back-home">
            <ArrowLeft size={14} aria-hidden="true" />
            {t("Back to Home")}
          </a>
        </div>
      }
    >
      <h1 className="auth-title" data-testid="register-title">
        {t("Create Your Suffa Matrimony Account")}
      </h1>
      <p className="auth-subtitle" data-testid="register-subtitle">
        {t("Begin your journey towards a meaningful marriage.")}
      </p>

      <form className="auth-form" noValidate onSubmit={onSubmit}>
        {serverError && (
          <p className="auth-error" data-testid="register-server-error" role="alert">
            {t(serverError)}
          </p>
        )}
        <fieldset className="auth-field auth-fieldset">
          <legend className="auth-label">{t("Who is this profile for?")}</legend>
          <div className="auth-choice-grid" data-testid="register-profile-for">
            {PROFILE_FOR_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`auth-choice${form.profileFor === opt ? " is-active" : ""}`}
                aria-pressed={form.profileFor === opt}
                data-testid={`register-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => update("profileFor", opt)}
              >
                {t(opt)}
              </button>
            ))}
          </div>
          {errors.profileFor && (
            <p className="auth-error" data-testid="register-profilefor-error">
              {t(errors.profileFor)}
            </p>
          )}
        </fieldset>

        <div className="auth-field">
          <label className="auth-label" htmlFor="register-name">
            {t("Full Name")}
          </label>
          <input
            id="register-name"
            type="text"
            className={field("fullName")}
            placeholder={t("Full name")}
            autoComplete="name"
            value={form.fullName}
            data-testid="register-name"
            aria-invalid={!!errors.fullName}
            onChange={(e) => update("fullName", e.target.value)}
          />
          {errors.fullName && (
            <p className="auth-error" data-testid="register-name-error">
              {t(errors.fullName)}
            </p>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="register-email">
            Email Address
          </label>
          <input
            id="register-email"
            type="email"
            className={field("email")}
            placeholder="you@example.com"
            autoComplete="email"
            value={form.email}
            data-testid="register-email"
            aria-invalid={!!errors.email}
            onChange={(e) => update("email", e.target.value)}
          />
          {errors.email && (
            <p className="auth-error" data-testid="register-email-error">
              {t(errors.email)}
            </p>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="register-mobile">
            Mobile Number
          </label>
          <input
            id="register-mobile"
            type="tel"
            className={field("mobile")}
            placeholder="Mobile number"
            autoComplete="tel"
            value={form.mobile}
            data-testid="register-mobile"
            aria-invalid={!!errors.mobile}
            onChange={(e) => update("mobile", e.target.value)}
          />
          {errors.mobile && (
            <p className="auth-error" data-testid="register-mobile-error">
              {t(errors.mobile)}
            </p>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="register-password">
            Password
          </label>
          <div className="auth-password">
            <input
              id="register-password"
              type={showPw ? "text" : "password"}
              className={field("password")}
              placeholder={t("At least 8 characters")}
              autoComplete="new-password"
              value={form.password}
              data-testid="register-password"
              aria-invalid={!!errors.password}
              onChange={(e) => update("password", e.target.value)}
            />
            <button
              type="button"
              className="auth-eye"
              data-testid="register-password-toggle"
              aria-label={showPw ? "Hide password" : "Show password"}
              onClick={() => setShowPw((v) => !v)}
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="auth-error" data-testid="register-password-error">
              {t(errors.password)}
            </p>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="register-confirm">
            {t("Confirm Password")}
          </label>
          <div className="auth-password">
            <input
              id="register-confirm"
              type={showConfirm ? "text" : "password"}
              className={field("confirmPassword")}
              placeholder={t("Re-enter your password")}
              autoComplete="new-password"
              value={form.confirmPassword}
              data-testid="register-confirm"
              aria-invalid={!!errors.confirmPassword}
              onChange={(e) => update("confirmPassword", e.target.value)}
            />
            <button
              type="button"
              className="auth-eye"
              data-testid="register-confirm-toggle"
              aria-label={showConfirm ? "Hide password" : "Show password"}
              onClick={() => setShowConfirm((v) => !v)}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="auth-error" data-testid="register-confirm-error">
              {t(errors.confirmPassword)}
            </p>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-check auth-check-start">
            <input
              type="checkbox"
              checked={form.agree}
              data-testid="register-agree"
              aria-invalid={!!errors.agree}
              onChange={(e) => update("agree", e.target.checked)}
            />
            <span>
              {t("I agree to the")}{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-legal-link"
                data-testid="register-terms-link"
              >
                Terms &amp; Conditions
              </a>{" "}
              {t("and")}{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-legal-link"
                data-testid="register-privacy-link"
              >
                Privacy Policy
              </a>{" "}
              {t("se sehmat hoon.")}
            </span>
          </label>
          {errors.agree && (
            <p className="auth-error" data-testid="register-agree-error">
              {t(errors.agree)}
            </p>
          )}
        </div>

        <button
          type="submit"
          className="btn btn-solid auth-submit"
          data-testid="register-submit"
          disabled={loading}
        >
          {loading ? t("Creating account…") : t("Create Account")}
        </button>
      </form>
    </AuthShell>
  );
};

export default Register;
